import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

interface EducationItem {
  degree: string;
  school: string;
  period: string;
  field: string;
  status?: string;
  link?: string;
}

const education: EducationItem[] = [
  {
    degree: 'education.mst.degree',
    school: 'education.mst.school',
    period: 'education.mst.period',
    field: 'education.mst.field',
    status: 'education.mst.status',
    link: 'https://fstbm.ac.ma/formation-initiale/mst-informatique-decisionnelle'
  },
  {
    degree: 'education.lst.degree',
    school: 'education.lst.school',
    period: 'education.lst.period',
    field: 'education.lst.field',
    link: 'https://fstbm.ac.ma/formation-initiale/lst-informatique'
  }
];

const EducationCard: React.FC<{ education: EducationItem; index: number }> = ({ education, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      className="card relative"
    >
      <div className="absolute -left-3 top-6 w-6 h-6 bg-secondary rounded-full" />
      <div className="absolute -left-1 top-7 w-2 h-2 bg-white rounded-full" />
      
      <div className="ml-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-secondary break-words">{t(education.degree)}</h3>
          {education.link && (
            <motion.a
              href={education.link}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="text-base-content/60 hover:text-primary transition-colors ml-2"
              aria-label={`View ${t(education.degree)} program`}
            >
              <FaExternalLinkAlt size={16} />
            </motion.a>
          )}
        </div>
        <div className="text-base-content/60 mb-2 break-words">{t(education.school)}</div>
        <div className="flex flex-wrap gap-2 text-sm text-base-content/60 mb-2">
          <span>{t(education.period)}</span>
          {education.status && (
            <>
              <span>•</span>
              <span>{t(education.status)}</span>
            </>
          )}
        </div>
        <div className="text-primary break-words">{t(education.field)}</div>
      </div>
    </motion.div>
  );
};

const Education: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section id="education" className="section-padding">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="heading-secondary mb-4 text-primary">{t('education.title')}</h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-secondary/30" />

          <div className="space-y-8">
            {education.map((edu, index) => (
              <EducationCard key={index} education={edu} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education; 